/* eslint-disable @typescript-eslint/no-unused-vars */
import type { QueryFunctionContext } from '@tanstack/react-query';

import { httpClient } from '@/domains/core/httpClient/httpClient';
import { HttpHeaders } from '@/domains/core/httpClient/httpHeaders';
import { httpQueryClient } from '@/domains/core/httpClient/queryClient';
import { getAppSetting } from '@/domains/core/settings/appSettings';
import { getPlatform } from '@/domains/core/settings/utils';

import type { WishlistDetailsContent, WishlistUnit } from './interfaces';
import {
  getAllMapper,
  getOneDetailMapper,
  getOneMapper,
} from './wishlistsMappers';

const WISHLISTS_PATH_ROUTE = () =>
  `${getAppSetting('MS_API_URL')}/api/v1/pro/wishlists`;

export const urls = {
  getAllUrl: () => WISHLISTS_PATH_ROUTE(),
  create: () => WISHLISTS_PATH_ROUTE(),
  getOne: (id: number | string) => `${WISHLISTS_PATH_ROUTE()}/${id}`,
  deleteOne: (id: number | string) => `${WISHLISTS_PATH_ROUTE()}/${id}`,
  editOne: (id: number | string) => `${WISHLISTS_PATH_ROUTE()}/${id}`,
  addProduct: (wishlistId: number | string, productId: number) =>
    `${WISHLISTS_PATH_ROUTE()}/${wishlistId}/products/${productId}`,
  updateProduct: (wishlistId: number | string, productId: number) =>
    `${WISHLISTS_PATH_ROUTE()}/${wishlistId}/products/${productId}`,
  deleteProduct: (wishlistId: number | string, productId: number) =>
    `${WISHLISTS_PATH_ROUTE()}/${wishlistId}/products/${productId}`,
};

const currentPlatform = getPlatform();
const commonHeaderAttribs = {
  [HttpHeaders.X_MM_PLATFORM]: currentPlatform.toUpperCase(),
};

let isWishListRevampService = false;
function applyABSetting(): Record<string, any> {
  if (isWishListRevampService) {
    return { ...commonHeaderAttribs, ['x-mm-revamp-wishlist']: true };
  }
  return commonHeaderAttribs;
}

export interface IWishlishServiceCalls {
  getAll(queryCtx: QueryFunctionContext): Promise<WishlistUnit[] | undefined>;
  getOne(
    queryCtx: QueryFunctionContext,
  ): Promise<WishlistDetailsContent | undefined>;
  create(name: string): Promise<WishlistUnit>;
  deleteOne(id: number | string): Promise<boolean>;
  editOne(id: number | string, name: string): Promise<boolean>;
  addProduct(
    wishlistId: number | string,
    productId: number,
    quantity: number,
  ): Promise<boolean>;
  updateProduct(
    wishlistId: number | string,
    productId: number,
    quantity: number,
  ): Promise<boolean>;
  deleteProduct(
    wishlistId: number | string,
    productId: number,
  ): Promise<boolean>;
  createShareLink(wishlistId: number | string): Promise<string>;
}

export const calls: IWishlishServiceCalls = {
  getAll: async ({ queryKey }: QueryFunctionContext<[string]>) => {
    const val = await httpClient.get(urls.getAllUrl(), {
      headers: applyABSetting(),
    });
    if (val.status === 306)
      return httpQueryClient.getQueryData<WishlistUnit[]>(queryKey);
    if (val.status >= 300) throw new Error('Unsuccessful status from backend');
    return getAllMapper(val.data);
  },
  getOne: async ({
    queryKey,
  }: QueryFunctionContext<[string, number | string]>) => {
    const [, wishlistId] = queryKey;
    const { data, status } = await httpClient.get(urls.getOne(wishlistId), {
      headers: applyABSetting(),
    });
    if (status === 306)
      return httpQueryClient.getQueryData<WishlistDetailsContent>(queryKey);
    if (status >= 300) throw new Error('Unsuccessful status from backend');
    return getOneDetailMapper(data === '' ? null : data);
  },
  create: async (name: string) => {
    const { data, status } = await httpClient.post(
      urls.create(),
      { name },
      {
        headers: applyABSetting(),
      },
    );
    if (status >= 300) throw new Error('Unsuccessful status from backend');
    return getOneMapper(data);
  },
  deleteOne: async (id: number | string) => {
    const { status } = await httpClient.delete(urls.deleteOne(id), {
      headers: applyABSetting(),
    });
    if (status >= 300) throw new Error('Unsuccessful status from backend');
    return true;
  },
  editOne: async (id: number | string, name: string) => {
    const { status } = await httpClient.patch(
      urls.editOne(id),
      { name },
      {
        headers: applyABSetting(),
      },
    );
    if (status >= 300) throw new Error('Unsuccessful status from backend');
    return true;
  },
  addProduct: async (
    wishlistId: number | string,
    productId: number,
    quantity: number,
  ) => {
    const { status } = await httpClient.patch(
      urls.addProduct(wishlistId, productId),
      { quantity },
      {
        headers: {
          'Content-Type': 'application/json',
          ...applyABSetting(),
        },
      },
    );
    if (status >= 300) throw new Error('Unsuccessful status from backend');
    return true;
  },
  updateProduct: async (
    wishlistId: number | string,
    productId: number,
    quantity: number,
  ) => {
    const { status } = await httpClient.put(
      urls.updateProduct(wishlistId, productId),
      { quantity },
      {
        headers: applyABSetting(),
      },
    );
    if (status >= 300) throw new Error('Unsuccessful status from backend');
    return true;
  },
  deleteProduct: async (wishlistId: number | string, productId: number) => {
    const { status } = await httpClient.delete(
      urls.addProduct(wishlistId, productId),
      {
        headers: applyABSetting(),
      },
    );
    if (status >= 300) throw new Error('Unsuccessful status from backend');
    return true;
  },
  // placeholder for the share basic
  createShareLink: async (wishlistId: number | string) =>
    `${wishlistId}_SHARE_LINK_PLACEHOLDER`,
} as const;

const names = (Object.keys(calls) as Array<keyof typeof calls>).reduce(
  (acc, val) => ({
    ...acc,
    [val]: val,
  }),
  {} as { [K in keyof typeof calls]: string },
);

/**
 * `WISHLISTS` - Helper function to unify all the http client api calls for ReactQuery
 */
export const wishlistsService = (isWishListRevampServiceFlag: boolean) => {
  isWishListRevampService = isWishListRevampServiceFlag;
  return {
    names,
    calls,
  };
};
